import Card from "./Card"
import SmallHeadingComponent from "./SmallHeadingComponent"
import { useAppDispatch, useAppSelector } from "../redux/store/hooks"
import {
    selectDashboardSelectedPatient,
    selectNotes
} from "../redux/ProfessionalDashboard"
import notNull from "../util/notNull"
import TextField from "./TextField"
import { useEffect, useState } from "react"
import Button from "./Button"
import {
    CreateProfessionalNoteSpecification,
    GetPatientsOfProfessionalResponsePayload,
    GetProfessionalNotesResponsePayload,
    GetProfessionalNotesSpecification
} from "pokal_api"
import { dispatchThunk } from "../redux/thunk/thunkify"
import { ArrayElement } from "../types/ArrayElement"
import mockPatient from "../onboarding/mock/mockPatient"
import {
    hidePatientOverviewNotes,
    mockPatientUsed,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../redux/Onboarding"

function NoteList() {
    const [selectedPatient, setSelectedPatient] =
        useState<ArrayElement<GetPatientsOfProfessionalResponsePayload> | null>(
            null
        )

    const fetchedPatient = useAppSelector(selectDashboardSelectedPatient)
    const mockedPatient = mockPatient()

    const running = useAppSelector(selectOnboardingIsRunningState)
    const step = useAppSelector(selectOnboardingStep)

    const [notes, setNotes] =
        useState<GetProfessionalNotesResponsePayload | null>(null)

    const fetchedNotes = useAppSelector(selectNotes)
    const mockedNotes: GetProfessionalNotesResponsePayload = []

    useEffect(() => {
        if (mockPatientUsed(running, step)) {
            setSelectedPatient(mockedPatient[0])
            setNotes(mockedNotes)
        } else {
            setSelectedPatient(fetchedPatient)
            setNotes(fetchedNotes)
        }
    }, [running, step, fetchedNotes, fetchedPatient])

    const [newNote, setNewNote] = useState<string>("")
    const dispatch = useAppDispatch()

    const [overwrite, setOverwrite] = useState<string | null>(null)

    function onAddNote() {
        if (newNote === "") return
        if (notNull(selectedPatient)) {
            CreateProfessionalNoteSpecification.request({
                patient: selectedPatient.id,
                content: newNote,
                overwrites: overwrite === null ? undefined : overwrite
            }).then(() => {
                dispatchThunk(dispatch, GetProfessionalNotesSpecification, {
                    patient: selectedPatient.id
                })
                setNewNote("")
                setOverwrite(null)
            })
        }
    }

    function sortedNotes(notes: GetProfessionalNotesResponsePayload): GetProfessionalNotesResponsePayload {
        return [...notes].sort((s1, s2) => (
            new Date(s1.createdAt).getTime() - new Date(s2.createdAt).getTime()
        )).map(
            n => ({
                id: n.id,
                content: n.content,
                createdAt: n.createdAt,
                overwrites: [...n.overwrites].sort((s1, s2) => (
                    new Date(s1.createdAt).getTime() - new Date(s2.createdAt).getTime()
                ))
            })
        )
    }

    return notNull(notes) ? (
        <Card blur={hidePatientOverviewNotes(running, step)}>
            <div className={"flex flex-col space-y-4"}>
                <SmallHeadingComponent>Notizen</SmallHeadingComponent>
                {notes.length === 0 && (
                    <div className={"italic"}>Keine Notizen verfügbar</div>
                )}
                {sortedNotes(notes).map((n) => (
                    <div key={n.id} className={"flex flex-col"}>
                        {n.overwrites.map((overwrittenNote) => (
                            <div
                                key={overwrittenNote.id}
                                className={
                                    "text-sm text-neutral-500 flex space-x-2"
                                }>
                                <div>Alte Fassung</div>
                                <div>
                                    [
                                    {new Date(
                                        overwrittenNote.createdAt
                                    ).toLocaleDateString("de")}
                                    ]
                                </div>
                                <div>-</div>
                                <div className={"line-through"}>
                                    {overwrittenNote.content}
                                </div>
                            </div>
                        ))}
                        <div className={"flex space-x-2"}>
                            <div className={"text-neutral-500 text-sm"}>
                                {new Date(n.createdAt).toLocaleDateString("de")}
                            </div>
                            {overwrite === null && (
                                <div
                                    onClick={() => setOverwrite(n.id)}
                                    className={
                                        "text-blue-600 hover:underline cursor-pointer text-sm"
                                    }>
                                    Überschreiben
                                </div>
                            )}
                            {overwrite !== null && overwrite === n.id && (
                                <div
                                    onClick={() => setOverwrite(null)}
                                    className={
                                        "text-rose-600 hover:underline cursor-pointer text-sm"
                                    }>
                                    Abbrechen
                                </div>
                            )}
                        </div>
                        <div>{n.content}</div>
                    </div>
                ))}
                <div className={"flex space-x-2"}>
                    <TextField
                        onEnter={onAddNote}
                        placeholder={
                            overwrite !== null
                                ? "Bestehende Notiz überschreiben"
                                : "Neue Notiz"
                        }
                        type={"text"}
                        inputState={[newNote, setNewNote]}
                    />
                    <Button onClick={onAddNote}>
                        {overwrite !== null
                            ? "Jetzt überschreiben"
                            : "Hinzufügen"}
                    </Button>
                </div>
            </div>
        </Card>
    ) : (
        <></>
    )
}

export default NoteList
